/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { WorkspaceObject } from 'jotai/workspace/WorkspaceObject';
import { userWorkspaceApi } from './UserWorkspaceApi';
import { WorkspaceServiceInterface } from './WorkspaceServiceInterface';
import { SanctioningBodyEnum, WorkspaceInfo, Workspace, RunType } from 'models/viewModels';

class UserWorkspaceService implements WorkspaceServiceInterface {
  cachePromise: Promise<Map<string, WorkspaceInfo>> | undefined;
  workspaceCache: Promise<WorkspaceObject | undefined> | WorkspaceObject | undefined;
  oldCachePromise: Promise<Map<string, string>> | undefined;

  private getCache(): Promise<Map<string, WorkspaceInfo>> {
    if (this.cachePromise) {
      return this.cachePromise;
    }
    this.cachePromise = this.getAllWorkspaceInfo().then((result) => result);
    return this.cachePromise;
  }

  //Required to not have to refactor the already not working Admin workspace code
  private getOldCache(): Promise<Map<string, string>> {
    if (this.oldCachePromise) {
      return this.oldCachePromise;
    }
    this.oldCachePromise = this.getWorkspaces().then((result) => result);
    return this.oldCachePromise;
  }

  async getWorkspaces(): Promise<Map<string, string>> {
    return userWorkspaceApi.getAllWorkspaces().then((response: Workspace[]) => {
      const workspaces = new Map<string, string>();
      response.forEach((workspace) => {
        workspaces.set(workspace.id, workspace.data);
      });
      return workspaces;
    });
  }

  private createWorkspace(workspace: string): WorkspaceObject {
    return JSON.parse(workspace) as WorkspaceObject;
  }

  async getAvailableWorkspaces(sessionType: RunType): Promise<WorkspaceObject[]> {
    const cache = await this.getOldCache();
    return Array.from(cache.values())
      .map((value) => this.createWorkspace(value))
      .filter((workspace) => !workspace.sessionTypes || workspace.sessionTypes.includes(sessionType));
  }

  async getAllWorkspaceIds(): Promise<string[]> {
    const cache = await this.getCache();
    return Array.from(cache.keys());
  }

  async getCurrentWorkspaceById(id: string): Promise<WorkspaceObject | undefined> {
    if (this.workspaceCache) {
      return this.workspaceCache;
    } else {
      this.workspaceCache = userWorkspaceApi
        .getWorkspace(id)
        .then((response) => response.data)
        .then((data) => {
          return data ? this.createWorkspace(data) : undefined;
        });
      return this.workspaceCache;
    }
  }

  async getWorkspaceById(id: string): Promise<WorkspaceObject | undefined> {
    return userWorkspaceApi
      .getWorkspace(id)
      .then((response) => response.data)
      .then((data) => {
        return data ? this.createWorkspace(data) : undefined;
      });
  }

  async getWorkspaceInfo(id: string): Promise<WorkspaceInfo | undefined> {
    const cache = await this.getCache();
    // allows for the workspace to be updated in the Jotai atom (otherwise it is treated like a reference and not updated in the atom)
    return cache.get(id) ? structuredClone(cache.get(id)) : undefined;
  }

  async updateWorkspace(workspace: WorkspaceObject, alternativeEnvironment?: boolean): Promise<boolean> {
    const status = await userWorkspaceApi
      .updateWorkspace(workspace, alternativeEnvironment)
      .then((response) => response.status);
    if (status) {
      const cache = await this.getCache();
      cache.set(workspace.id, {
        id: workspace.id,
        name: workspace.name,
        slug: workspace.slug,
        owner: workspace.owner,
        sanctioningBody: workspace.sanctioningBody || SanctioningBodyEnum.UNKNOWN,
        shared: workspace.shared || false,
        sharedDomains: workspace.sharedDomains || [],
      });
      this.workspaceCache = workspace;
    }
    return status;
  }

  async updateWorkspaceInfo(
    workspace: WorkspaceInfo | WorkspaceObject,
    alternativeEnvironment?: boolean
  ): Promise<boolean> {
    const workspaceObj = await this.getWorkspaceById(workspace.id);
    if (workspaceObj) {
      const newWorkspace: WorkspaceObject = structuredClone({
        ...workspaceObj,
        ...workspace,
        // Fixes the mismatch in enums right now
        sanctioningBody: workspaceObj.sanctioningBody || SanctioningBodyEnum.UNKNOWN,
      });
      const status = await userWorkspaceApi
        .updateWorkspace(newWorkspace, alternativeEnvironment)
        .then((response) => response.status);
      if (status) {
        const cache = await this.getCache();
        cache.set(workspace.id, {
          id: workspace.id,
          name: workspace.name,
          slug: workspace.slug,
          owner: workspace.owner,
          // Fixes the mismatch in enums right now
          sanctioningBody: workspace.sanctioningBody || SanctioningBodyEnum.UNKNOWN,
          shared: workspace.shared || false,
          sharedDomains: workspace.sharedDomains || [],
        });
      }
      return status;
    }
    return false;
  }

  async deleteWorkspace(workspaceId: string): Promise<boolean> {
    const status = await userWorkspaceApi.deleteWorkspace(workspaceId).then((response) => response.status);
    if (status) {
      const cache = await this.getCache();
      cache.delete(workspaceId);
    }
    return status;
  }

  async getAvailableSanctioningBodyWorkspaces(sanctioningBody: SanctioningBodyEnum): Promise<WorkspaceObject[]> {
    return userWorkspaceApi.getAvailableWorkspacesBySanctioningBody(sanctioningBody).then((response: Workspace[]) => {
      return [...response].map((workspace) => this.createWorkspace(workspace.data));
    });
  }

  async getAllWorkspaceInfo(): Promise<Map<string, WorkspaceInfo>> {
    return userWorkspaceApi.getAllWorkspaceInfo().then((response: WorkspaceInfo[]) => {
      const workspaces = new Map<string, WorkspaceInfo>();
      response.forEach((workspace) => {
        workspaces.set(workspace.id, workspace);
      });
      return workspaces;
    });
  }
}

export const userWorkspaceService = new UserWorkspaceService();
